import { useCallback } from 'react'
import { useQueryClient } from 'react-query'
import { useRegenerateBrandPlanScheduleTopics } from '../../../../../hooks/queries/brandPlans/useRegenerateBrandPlanScheduleTopics'
import { useRegenerateBrandPlanContent } from '../../../../../hooks/queries/brandPlans/useRegenerateBrandPlanContent'
import { optimisticRemoveFilesFromInfiniteLists } from '../../../../../hooks/queries/files/cacheUtils'
import { useCurrentWorkspaceId } from '../../../../../hooks/useCurrentWorkspaceId'
import type { BrandPlan, BrandPlanSchedule } from '../../../../../types'

const UPCOMING_SCHEDULES_TO_REGENERATE = 3

interface UseBrandKitRegenerationParams {
  brandPlan: BrandPlan | undefined
  plannedSchedules: BrandPlanSchedule[] | undefined
  shouldRegenerateTopics: boolean
  fileIdsToRemove: string[]
}

type RegenerateResult =
  | { type: 'topics'; scheduleId: number }
  | { type: 'content'; response: { regenerated_count: number; message: string } }

const hasPlannedSchedules = (schedules: BrandPlanSchedule[] | undefined): schedules is BrandPlanSchedule[] =>
  Boolean(schedules && schedules.length > 0)

export const useBrandKitRegeneration = ({
  brandPlan,
  plannedSchedules,
  shouldRegenerateTopics,
  fileIdsToRemove,
}: UseBrandKitRegenerationParams) => {
  const queryClient = useQueryClient()
  const workspaceId = useCurrentWorkspaceId()
  const { mutateAsync: regenerateTopics, isLoading: isRegeneratingTopics } = useRegenerateBrandPlanScheduleTopics()
  const { mutateAsync: regenerateContent, isLoading: isRegeneratingContent } = useRegenerateBrandPlanContent()

  const regenerate = useCallback(async (): Promise<RegenerateResult> => {
    if (!brandPlan) {
      throw new Error('Brand plan not found')
    }

    if (shouldRegenerateTopics && hasPlannedSchedules(plannedSchedules)) {
      const schedulesToRegenerate = [...plannedSchedules]
        .sort((a, b) => (a.start_date || '').localeCompare(b.start_date || ''))
        .slice(0, UPCOMING_SCHEDULES_TO_REGENERATE)

      await Promise.all(
        schedulesToRegenerate.map((schedule) =>
          regenerateTopics({
            scheduleId: schedule.id,
            regenerateContentAfter: true,
            preview: false,
          }),
        ),
      )

      if (fileIdsToRemove.length > 0) {
        optimisticRemoveFilesFromInfiniteLists(queryClient, workspaceId, fileIdsToRemove)
      }

      return { type: 'topics', scheduleId: schedulesToRegenerate[0].id }
    }

    const response = await regenerateContent({
      brandPlanId: brandPlan.id,
      body: {
        topic: '',
        content_scope: 'current_batch',
      },
    })

    if (fileIdsToRemove.length > 0) {
      optimisticRemoveFilesFromInfiniteLists(queryClient, workspaceId, fileIdsToRemove)
    }

    return { type: 'content', response }
  }, [
    brandPlan,
    plannedSchedules,
    shouldRegenerateTopics,
    regenerateTopics,
    regenerateContent,
    fileIdsToRemove,
    queryClient,
    workspaceId,
  ])

  const isLoading = isRegeneratingTopics || isRegeneratingContent

  return {
    regenerate,
    isLoading,
  }
}
